export interface ParsedFormationPostError {
  error_code: string;
  user_hint: string;
  error_trace: string;
}

function stringifyResponseData(data: unknown): string {
  if (data === undefined || data === null) {
    return '';
  }
  if (typeof data === 'string') {
    return data;
  }
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function extractErrorMessage(data: Record<string, unknown> | undefined): string {
  if (!data) {
    return '';
  }
  const { error } = data;
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    return error
      .map((item) => (typeof item === 'string' ? item : JSON.stringify(item)))
      .join('\n');
  }
  if (error && typeof error === 'object') {
    const body = (error as { body?: unknown }).body;
    if (typeof body === 'string') {
      return body;
    }
    return JSON.stringify(error, null, 2);
  }
  if (typeof data.result === 'string') {
    return data.result;
  }
  return stringifyResponseData(data);
}

export function parseAccountingSheetPostError(
  error: unknown,
  t: (key: string) => string,
): ParsedFormationPostError {
  const axiosError = error as {
    response?: { status?: number; data?: Record<string, unknown> };
    message?: string;
  };
  const status = axiosError.response?.status;
  const data = axiosError.response?.data;
  const errorText = extractErrorMessage(data);
  const errorTrace = stringifyResponseData(data) || String(axiosError.message ?? error);

  if (status === 401 || status === 403) {
    return {
      error_code: 'POST_AUTH_DENIED',
      user_hint: t('formation_sheet_error_post_auth_denied'),
      error_trace: errorTrace,
    };
  }

  const lower = errorText.toLowerCase();
  if (status === 400 && lower.includes('no applications found')) {
    return {
      error_code: 'POST_NO_APPLICATIONS',
      user_hint: t('formation_sheet_error_post_no_applications'),
      error_trace: errorTrace,
    };
  }
  if (status === 400 && lower.includes("hr doesn't have any coordinators")) {
    return {
      error_code: 'POST_NO_COORDINATORS',
      user_hint: t('formation_sheet_error_post_no_coordinators'),
      error_trace: errorTrace,
    };
  }
  if (status === 500 && Array.isArray(data?.error)) {
    return {
      error_code: 'POST_JBPM_START_FAILED',
      user_hint: t('formation_sheet_error_post_jbpm_start_failed'),
      error_trace: errorTrace,
    };
  }
  if (status !== undefined && status >= 500) {
    return {
      error_code: 'POST_INTERNAL_ERROR',
      user_hint: t('formation_sheet_error_post_internal_error'),
      error_trace: errorTrace,
    };
  }

  return {
    error_code: 'POST_UNKNOWN',
    user_hint: errorText || t('formation_sheet_error_post_internal_error'),
    error_trace: errorTrace,
  };
}
